import type { FeatureFlagService } from '.';
import { signal, computed, effect } from '@preact/signals-react';
import type { ReadonlySignal, Signal } from '@preact/signals-react';
import { injectable, inject, skipAutoDispose } from 'src/features/ioc';
import { allFlags as allFlagRegistry } from 'src/lib/featureFlags/allFlags';
import type { FeatureFlag } from 'src/lib/featureFlags/FeatureFlag';
import type { ReactBindings } from 'src/types/bindings';

/**
 * Per-flag state entry containing signals and override information.
 */
interface FlagEntry {
    /** Computed signal that reads current value from Redux store */
    storeSignal: ReadonlySignal<unknown>;
    /** Whether an override is currently set (separate from override value to allow undefined as override) */
    hasOverride: boolean;
    /** Local override value (only valid when hasOverride is true) */
    override: unknown;
    /** Pre-computed final signal for this flag (combines store + override) */
    computed: ReadonlySignal<unknown>;
    /** Pre-computed hasOverride signal (reads from state.hasOverride) */
    hasOverrideSignal: ReadonlySignal<boolean>;
}

/**
 * Centralized state for all feature flags.
 */
interface FeatureFlagState {
    flags: Map<string, FlagEntry>;
}

/**
 * Actions for state updates.
 */
type FeatureFlagAction =
    | { type: 'SYNC_STORE_FLAGS'; storeFlags: Record<string, unknown> }
    | { type: 'SET_OVERRIDE'; flagKey: string; value: unknown }
    | { type: 'CLEAR_OVERRIDE'; flagKey: string };

/**
 * Service providing reactive access to feature flags via Preact signals.
 * Uses a centralized state reducer pattern with signals, enabling:
 * - Dynamic flag addition/removal as flags appear/disappear in store
 * - Per-flag state: override value, store value, and final computed signal
 * - Stored computed signals (no separate cache needed)
 * - Clean state updates via dispatch
 */
@injectable()
export class FeatureFlagServiceImpl implements FeatureFlagService {
    readonly [skipAutoDispose] = true;
    /** Centralized state containing all flag entries */
    #state: Signal<FeatureFlagState>;
    /** Store reference for creating computed signals */
    #store: ReactBindings['Store'];
    #log: ReturnType<ReactBindings['Logger']['getSubLogger']>;
    /** Map of flag keys to their default values from allFlags registry */
    #defaultValues: Map<string, unknown>;
    /** Dispose function for the store sync effect */
    #disposeEffect: (() => void) | undefined;

    constructor(@inject('Store') store: ReactBindings['Store'], @inject('Logger') logger: ReactBindings['Logger']) {
        this.#store = store;
        this.#log = logger.getSubLogger({ name: 'FeatureFlagService' });

        // Build map of default values from the allFlags registry
        this.#defaultValues = new Map<string, unknown>(
            Object.values(allFlagRegistry).map((flag) => [flag.key, flag.defaultValue] as const),
        );

        // Initialize state with current store flags
        this.#state = signal(this.#createInitialState());

        // Use effect to sync store changes -> dispatch SYNC_STORE_FLAGS
        this.#disposeEffect = effect(() => {
            const storeFlags = store.signal.value.featureFlags.flags;
            this.#dispatch({ type: 'SYNC_STORE_FLAGS', storeFlags });
        });

        if (TRACE) {
            const initialFlagEntries = Object.values(allFlagRegistry).map((flag) => ({
                key: flag.key,
                value: flag.select(store.getState()),
            }));
            this.#log.trace('FeatureFlagService initialized', {
                registeredFlagCount: this.#defaultValues.size,
                flags: initialFlagEntries,
            });
        }
    }

    /**
     * Creates the initial state based on current store flags.
     */
    #createInitialState(): FeatureFlagState {
        const storeFlags = this.#store.signal.value.featureFlags.flags;
        const flags = new Map<string, FlagEntry>();

        for (const key of Object.keys(storeFlags)) {
            if (this.#defaultValues.has(key)) {
                flags.set(key, this.#createFlagEntry(key));
            }
        }

        return { flags };
    }

    /**
     * Creates a new FlagEntry for a given flag key.
     * The storeSignal is a computed that reads directly from the store.
     */
    #createFlagEntry(flagKey: string): FlagEntry {
        const defaultValue = this.#defaultValues.get(flagKey);

        // storeSignal is a computed that reads from the store - never mutated
        // Applies default value fallback when store value is null/undefined
        const storeSignal = computed(() => {
            const storeValue = this.#store.signal.value.featureFlags.flags[flagKey];
            return storeValue ?? defaultValue;
        });

        return {
            storeSignal,
            hasOverride: false,
            override: undefined,
            // Computed reads hasOverride/override from state (reactive to state changes)
            // and storeSignal (reactive to store changes)
            computed: computed(() => {
                const entry = this.#state.value.flags.get(flagKey);
                if (entry?.hasOverride) {
                    return entry.override;
                }
                return storeSignal.value;
            }),
            hasOverrideSignal: computed(() => {
                const entry = this.#state.value.flags.get(flagKey);
                return entry?.hasOverride ?? false;
            }),
        };
    }

    /**
     * Dispatches an action to update the state.
     */
    #dispatch(action: FeatureFlagAction): void {
        switch (action.type) {
            case 'SYNC_STORE_FLAGS': {
                const currentFlags = this.#state.value.flags;
                const storeKeys = new Set(Object.keys(action.storeFlags).filter((k) => this.#defaultValues.has(k)));

                // Check if flags need to be added or removed
                const currentKeys = new Set(currentFlags.keys());
                const toAdd = storeKeys
                    .values()
                    .filter((k) => !currentKeys.has(k))
                    .toArray();
                const toRemove = currentKeys
                    .values()
                    .filter((k) => !storeKeys.has(k))
                    .toArray();

                if (toAdd.length === 0 && toRemove.length === 0) {
                    return; // No structural changes needed - storeSignals auto-update via computed
                }

                // Create new state with added/removed flags
                const newFlags = new Map<string, FlagEntry>();
                for (const key of storeKeys) {
                    const existing = currentFlags.get(key);
                    newFlags.set(key, existing ?? this.#createFlagEntry(key));
                }
                this.#state.value = { flags: newFlags };
                break;
            }
            case 'SET_OVERRIDE': {
                const entry = this.#state.value.flags.get(action.flagKey);
                if (entry) {
                    const newFlags = new Map(this.#state.value.flags);
                    newFlags.set(action.flagKey, { ...entry, hasOverride: true, override: action.value });
                    this.#state.value = { flags: newFlags };
                }
                break;
            }
            case 'CLEAR_OVERRIDE': {
                const entry = this.#state.value.flags.get(action.flagKey);
                if (entry?.hasOverride) {
                    const newFlags = new Map(this.#state.value.flags);
                    newFlags.set(action.flagKey, { ...entry, hasOverride: false, override: undefined });
                    this.#state.value = { flags: newFlags };
                }
                break;
            }
        }
    }

    getSignal<T>(flag: FeatureFlag<T>): ReadonlySignal<T> {
        const entry = this.#state.value.flags.get(flag.key);
        if (!entry) {
            // Flag is not configured in allFlags - this is a programming error
            this.#log.error('Attempted to access unconfigured feature flag', { flagKey: flag.key });
            throw new Error(`Feature flag "${flag.key}" is not configured. Add it to allFlags.`);
        }
        return entry.computed as ReadonlySignal<T>;
    }

    getValue<T>(flag: FeatureFlag<T>): T {
        return this.getSignal(flag).value;
    }

    setOverride(flagKey: string, value: unknown): void {
        if (!this.#defaultValues.has(flagKey)) {
            this.#log.warn('Attempted to set override for unconfigured flag', { flagKey });
            return;
        }
        this.#dispatch({ type: 'SET_OVERRIDE', flagKey, value });
    }

    clearOverride(flagKey: string): void {
        this.#dispatch({ type: 'CLEAR_OVERRIDE', flagKey });
    }

    hasOverride(flagKey: string): ReadonlySignal<boolean> {
        if (TRACE) {
            this.#log.trace('hasOverride called', { flagKey, isRegistered: this.#defaultValues.has(flagKey) });
        }

        const entry = this.#state.value.flags.get(flagKey);
        if (!entry) {
            // Return a constant false signal for unconfigured flags
            if (TRACE) {
                this.#log.trace('hasOverride called for unconfigured flag', { flagKey });
            }
            return signal(false);
        }

        return entry.hasOverrideSignal;
    }

    [Symbol.dispose](): void {
        this.#disposeEffect?.();
    }
}
